import { AlertTriangle } from 'lucide-react';
import { Panel, StatTile, tbl } from '../ui';
import { cn } from '../../utils/cn';
import type { ServiceTimeReport, MerchantWaitReport } from '../../services/analytics.api';

export default function ServiceTimePanels({ svc, wait }: { svc?: ServiceTimeReport; wait?: MerchantWaitReport }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Service time by pickup size" subtitle="Arrival → last package scanned">
        <div className={tbl.wrap}>
          <table className={tbl.table}>
            <thead>
              <tr className={tbl.headRow}>
                <th className={tbl.th}>Orders</th>
                <th className={cn(tbl.th, tbl.num)}>Legs</th>
                <th className={cn(tbl.th, tbl.num)}>Median</th>
                <th className={cn(tbl.th, tbl.num)}>Average</th>
                <th className={cn(tbl.th, tbl.num)}>s / package</th>
              </tr>
            </thead>
            <tbody>
              {(svc?.buckets ?? []).map((b) => (
                <tr key={b.bucket} className={tbl.row}>
                  <td className={tbl.td}>{b.bucket}</td>
                  <td className={cn(tbl.td, tbl.num)}>{b.legs}</td>
                  <td className={cn(tbl.td, tbl.num)}>{b.service_p50_min} m</td>
                  <td className={cn(tbl.td, tbl.num)}>{b.service_avg_min} m</td>
                  <td className={cn(tbl.td, tbl.num)}>{b.per_package_p50_s}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[12px] text-gray-500 text-pretty">
          {svc?.legs ?? 0} completed merchant legs · {svc?.orders ?? 0} orders. Medians under 11 orders are
          distorted by late arrival taps ({svc?.batch_tapped_pct ?? 0}% of legs) — trust averages and p90 there.
        </p>
      </Panel>

      <Panel title="Wait at merchant & data quality">
        <div className="grid grid-cols-2 gap-3">
          <StatTile variant="inset" label="Visits with measurable wait" value={wait?.measurable_pct ?? 0} unit="%" sub={`${wait?.measurable ?? 0} of ${wait?.visits ?? 0} GPS visits`} />
          <StatTile variant="inset" label="Median wait (when real)" value={wait?.wait_p50_min ?? 0} unit="min" sub={`p90 ${wait?.wait_p90_min ?? 0} min · ${wait?.over_5min ?? 0} over 5 min`} />
          <StatTile variant="inset" label="Legs in 10–20 min band" value={svc?.in_band_pct ?? 0} unit="%" />
          <StatTile variant="inset" label="Legs over 20 min" value={svc?.over_band_pct ?? 0} unit="%" />
        </div>
        <div className="mt-4 flex gap-2 rounded-xl bg-amber-50 p-3 text-[12px] leading-relaxed text-amber-900/80">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          <span>
            Drivers scan every package (77% of orders are driver-scanned), but the app writes all
            “collected” timestamps in one batch at completion — scan pace comes from task timings, and{' '}
            {svc?.batch_tapped_pct ?? 0}% of legs have the “arrived” tap at the end of the visit.
          </span>
        </div>
      </Panel>
    </div>
  );
}
